import type { ViewerClient } from "../../client/ViewerClient";

/**
 * Which model loads arrived WITH a saved scene — the signal the "Apply
 * default materials" dialog reads so it never asks about a piece whose scene
 * already says what every part is made of.
 *
 * Why here and not in the editor store's `ui` slice: the code that restores a
 * saved scene (`StudioModelLoader` in StudioPage) renders OUTSIDE the
 * EditorProvider — it is a sibling of the shell, so it can start fetching
 * while the chrome mounts — and cannot reach the store. A module both sides
 * import can be written synchronously before the model is added, which is
 * what makes the timing provable (below).
 *
 * Keyed by `ViewerClient`, not global: the viewer mints model ids per
 * INSTANCE ("model_1", "model_2" …), so leaving the studio and coming back
 * starts a fresh viewer whose `model_1` is a different piece. A per-client
 * ledger dies with its viewer (WeakMap) and cannot match the next one's ids.
 *
 * ## Why the dialog cannot flash over a restored scene
 *
 * A restore calls `beginSceneRestore` BEFORE it posts the RPC that adds the
 * model. The viewer can only mint that model's id, and emit `modelsChanged`,
 * after receiving that RPC, so the host cannot see the new id before the
 * window is open. The restore then `claim`s the id `addModelUrl` returned
 * before it calls `end`. So whenever the dialog first sees the id, either the
 * window is still open or the id is claimed — there is no instant at which
 * the load reads as new. And the verdict is decided ONCE per model set and
 * remembered, so nothing that happens later (the restore finishing, a
 * StrictMode double render, Present mode remounting the chrome) can flip it.
 */

export type LoadVerdict =
  /** A piece arrived on its own: ask what its parts are made of. */
  | "ask"
  /** It came with a saved scene (or a dev boot scene): the scene owns its materials. */
  | "restored"
  /** No new piece: a part was removed, or something was added NEXT TO a piece
   *  already on screen (a generated primitive from Advanced). */
  | "skip"
  /** The dialog was answered for this load. */
  | "answered";

interface Ledger {
  /** Restores in flight. While > 0, every model that appears is part of one. */
  restoring: number;
  /** Model ids a restore brought in. The viewer never reuses an id. */
  restored: Set<string>;
  /** Every model id a verdict has been given for. */
  seen: Set<string>;
  /** Verdict per model set (sorted ids), decided once. */
  verdicts: Map<string, LoadVerdict>;
}

const ledgers = new WeakMap<ViewerClient, Ledger>();

function ledgerOf(client: ViewerClient): Ledger {
  let l = ledgers.get(client);
  if (!l) {
    l = { restoring: 0, restored: new Set(), seen: new Set(), verdicts: new Map() };
    ledgers.set(client, l);
  }
  return l;
}

/** One identity per set of models on screen. */
export function modelSetKey(modelIds: readonly string[]): string {
  return [...modelIds].sort().join("|");
}

export interface SceneRestore {
  /** Record the model(s) this restore brought in. Call before `end`. */
  claim(modelIds: string | readonly string[]): void;
  /** Close the window. Safe to call twice; call it in a `finally`. */
  end(): void;
}

/**
 * Open a restore window. Call it synchronously BEFORE the RPC that adds the
 * model (or applies a scene document that can add models).
 */
export function beginSceneRestore(client: ViewerClient): SceneRestore {
  const l = ledgerOf(client);
  l.restoring++;
  let open = true;
  return {
    claim(modelIds) {
      for (const id of typeof modelIds === "string" ? [modelIds] : modelIds) l.restored.add(id);
    },
    end() {
      if (!open) return;
      open = false;
      l.restoring--;
    },
  };
}

/** A model on screen, as far as the verdict is concerned (`ModelInfo`). */
export interface LoadedModel {
  id: string;
  /** v65 — the model's file carried its own scene ("Ring + scene"). */
  embeddedScene?: string;
}

/**
 * A model whose own file is bringing (or brought) its scene. The viewer
 * reports "pending" already in the FIRST `modelsChanged` for such a model, so
 * this is known the first time the dialog sees it — the same no-flash
 * guarantee the restore window gives. "ignored" (the host passed its own saved
 * scene instead) and "failed" are not restores: the host's scene, if any, has
 * its own window open; otherwise the piece is asked about as usual.
 */
const carriesScene = (m: LoadedModel) => m.embeddedScene === "pending" || m.embeddedScene === "applied";

/**
 * What the set of models now on screen is. Decided the first time a set is
 * seen and remembered, so calling it again (StrictMode, a remount) returns the
 * same answer.
 *
 * `bootScene` — the page itself booted the viewer with a scene (`?vjson=` on
 * /dev/studio): the FIRST load this viewer shows came with it.
 */
export function loadVerdict(
  client: ViewerClient,
  models: readonly LoadedModel[],
  opts: { bootScene?: boolean } = {},
): LoadVerdict | null {
  if (models.length === 0) return null;
  const modelIds = models.map((m) => m.id);
  const l = ledgerOf(client);
  const key = modelSetKey(modelIds);
  const known = l.verdicts.get(key);
  if (known) return known;

  const fresh = models.filter((m) => !l.seen.has(m.id));
  const firstLoad = l.seen.size === 0;
  for (const m of fresh) l.seen.add(m.id);

  let verdict: LoadVerdict;
  if (fresh.length < models.length) verdict = "skip";
  else if (
    l.restoring > 0 ||
    fresh.some((m) => l.restored.has(m.id) || carriesScene(m)) ||
    (firstLoad && opts.bootScene)
  )
    verdict = "restored";
  else verdict = "ask";
  l.verdicts.set(key, verdict);
  return verdict;
}

/** The dialog was answered (applied or kept) for this set of models. */
export function settleLoad(client: ViewerClient, modelIds: readonly string[]): void {
  ledgerOf(client).verdicts.set(modelSetKey(modelIds), "answered");
}

// ── what each part was when its piece loaded ─────────────────────────────

/**
 * The kind (metal or stone) the viewer reported for each part the FIRST time
 * this client listed it — i.e. as the piece loaded, before anything the user
 * applied could re-file it. The customer configurator reads it to keep a
 * slot's kind stable (`features/vault/configurator.ts` `slotKindOf`): a pearl
 * or a metal file on the stones makes the viewer report them as metal from
 * then on, and the bar must still know they are stones.
 *
 * Group ids are derived from the model's content (contract v47), so a part
 * keeps its id across reloads of the same file and its first sighting is its
 * load-time kind. Per client, like the ledger above: a new viewer starts over.
 */
const loadKinds = new WeakMap<ViewerClient, Map<string, "metal" | "gem">>();

/** Record every part not seen before, with the kind it has now. */
export function noteLoadKinds(client: ViewerClient, groups: readonly { id: string; kind: string }[] | undefined): void {
  if (!groups?.length) return;
  let m = loadKinds.get(client);
  if (!m) {
    m = new Map();
    loadKinds.set(client, m);
  }
  for (const g of groups) {
    if (!m.has(g.id) && (g.kind === "metal" || g.kind === "gem")) m.set(g.id, g.kind);
  }
}

/** The kind a part had when its piece loaded, if this client has seen it. */
export function loadKindOf(client: ViewerClient, groupId: string): "metal" | "gem" | undefined {
  return loadKinds.get(client)?.get(groupId);
}
